import { setToken, getToken, removeToken } from '../../utils/authStorage'

const PING_INTERVAL_MS = 25000
const RECV_TIMEOUT_MS = 75000
const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 30000

const state = {
  ws: null,
  wsToken: getToken() || '',
  msg: '',
  isSocket: false,
  pingTimer: null,
  watchTimer: null,
  reconnectTimer: null,
  reconnectAttempt: 0,
  lastRecvAt: 0,
  connecting: false,
  intentionalClose: false,
  num: 0
}

function reconnectDelay(attempt) {
  const base = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * Math.pow(2, Math.min(attempt, 5)))
  return base + Math.floor(Math.random() * 400)
}

const mutations = {
  WEBSOCKET_INIT(state) {
    let url = process.env['VUE_APP_WSS']
    if (state.wsToken) {
      url = `${url}/im?token=${state.wsToken}`
    }
    if (!state.wsToken) return
    if (state.connecting) return
    if (state.ws && state.isSocket) return
    if (state.ws && !state.isSocket) {
      try {
        state.intentionalClose = true
        state.ws.close()
      } catch (e) {
        // ignore close race
      }
      state.ws = null
    }

    state.connecting = true
    state.intentionalClose = false
    const ws = new WebSocket(url)

    ws.onopen = () => {
      console.log('[ws] open')
      state.connecting = false
      state.isSocket = true
      state.reconnectAttempt = 0
      state.lastRecvAt = Date.now()
      clearTimeout(state.reconnectTimer)
      state.reconnectTimer = null
      this.commit('ws/sendSockePing')
      this.commit('ws/startRecvWatch')
    }

    ws.onmessage = (msg) => {
      state.lastRecvAt = Date.now()
      if (!msg.data) return
      try {
        const data = JSON.parse(msg.data)
        console.log(data)
        state.msg = msg.data
        if (data.code === 401) {
          this.commit('ws/logoutMu')
        }
      } catch (e) {
        // ignore non-json
      }
    }

    ws.onerror = (err) => {
      console.log('[ws] error', err)
    }

    ws.onclose = () => {
      console.log('[ws] close')
      state.connecting = false
      state.isSocket = false
      state.ws = null
      clearTimeout(state.pingTimer)
      state.pingTimer = null
      clearInterval(state.watchTimer)
      state.watchTimer = null
      if (state.intentionalClose) {
        state.intentionalClose = false
        return
      }
      this.commit('ws/scheduleReconnect')
    }

    state.ws = ws
  },
  logoutMu(state) {
    clearTimeout(state.pingTimer)
    clearInterval(state.watchTimer)
    clearTimeout(state.reconnectTimer)
    state.pingTimer = null
    state.watchTimer = null
    state.reconnectTimer = null
    removeToken()
    state.intentionalClose = true
    if (state.ws) {
      try {
        state.ws.close()
      } catch (e) {
        // ignore close race
      }
    }
    state.ws = null
    state.wsToken = ''
    state.isSocket = false
    state.connecting = false
  },
  quit(state) {
    state.intentionalClose = true
    clearTimeout(state.reconnectTimer)
    state.reconnectTimer = null
    if (state.ws) {
      try {
        state.ws.close()
      } catch (e) {
        // ignore close race
      }
    }
  },
  scheduleReconnect(state) {
    if (!state.wsToken) return
    if (state.reconnectTimer) return
    const delay = reconnectDelay(state.reconnectAttempt || 0)
    state.reconnectAttempt = (state.reconnectAttempt || 0) + 1
    console.log('[ws] reconnect in', delay, 'attempt', state.reconnectAttempt)
    state.reconnectTimer = setTimeout(() => {
      state.reconnectTimer = null
      this.commit('ws/WEBSOCKET_INIT')
    }, delay)
  },
  startRecvWatch(state) {
    clearInterval(state.watchTimer)
    state.watchTimer = setInterval(() => {
      if (!state.isSocket) return
      if (state.lastRecvAt && Date.now() - state.lastRecvAt > RECV_TIMEOUT_MS) {
        console.log('[ws] recv timeout, force close')
        state.intentionalClose = false
        try {
          state.ws && state.ws.close()
        } catch (e) {
          // ignore close race
        }
      }
    }, 10000)
  },
  sendSockePing() {
    const data = { action: 'ClientPing', data: '123', push: [] }
    this.commit('ws/sendSockeTask', JSON.stringify(data))
    this.commit('ws/continuePing')
  },
  continuePing(state) {
    clearTimeout(state.pingTimer)
    state.pingTimer = setTimeout(() => {
      this.commit('ws/sendSockePing')
    }, PING_INTERVAL_MS)
  },
  sendSockeTask(state, data) {
    if (!state.ws || state.ws.readyState !== WebSocket.OPEN) {
      this.commit('ws/scheduleReconnect')
      return
    }
    try {
      state.ws.send(data)
    } catch (e) {
      console.log('[ws] send fail', e)
      this.commit('ws/scheduleReconnect')
    }
  },
  SET_TOKEN(state, token) {
    state.wsToken = token
    setToken(token)
  }
}

const actions = {
  WEBSOCKET_INIT({ commit }, url) {
    commit('WEBSOCKET_INIT', url)
  },
  WEBSOCKET_SEND({ commit }, data) {
    commit('sendSockeTask', data)
  },
  setToken({ commit }, data) {
    commit('SET_TOKEN', data)
    commit('WEBSOCKET_INIT')
  },
  logout({ commit }) {
    commit('logoutMu')
  },
  quitApp({ commit }) {
    commit('quit')
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
